import { Tooltip } from "@ark-ui/solid";
import { type Component, For, Index, Match, Show, Switch } from "solid-js";
import { Portal } from "solid-js/web";
import { twJoin } from "tailwind-merge";
import InfoIcon from "~/Icons/18_Icon_Info.svg?component-solid";
import CheckedIcon from "~/Icons/24_Icon_Checked.svg?component-solid";
import EnabledIcon from "~/Icons/24_Icon_CheckMark.svg?component-solid";
import TipFrameIcon from "~/Icons/TipFrameIcon.svg?component-solid";
import { PricingPageMethods } from "~/solidJs/pages/PricingPage";
import { PricingPageContext } from "~/solidJs/pages/PricingPage/methods";
import { useCustomContext } from "~/solidJs/shared/helpers";
import { UserSubscriptionPlanEnum } from "~/solidJs/shared/helpers/types/IUser";
import { StyledTooltip } from "~/solidJs/shared/ui/atoms/Tooltip";
import type { ProductsApi } from "../../shared/api/ProductsApi";
import { getUserStore } from "../HeaderMain/models";
import { getActiveCostByInterval, type IProduct } from "../PremiumPopUp/helpers/IProducts";
import { TariffBtnModeEnum, tariffLokUp } from "../PremiumPopUp/helpers/tariffLookUpModes";
import { PremiumPopUpContext } from "../PremiumPopUp/models/premiumPopUpContext";
import { CheckoutButtonFeatureV2 } from "../PremiumPopUp/ui";
export type PricingTableFeatureTarifs = {
	nameId: string;
} & (
	| {
			dataType: "string";
			value: string;
	  }
	| {
			dataType: "bool";
			value: boolean;
	  }
);
export type PricingTableFeature = {
	name: string;
	tariffs: PricingTableFeatureTarifs[];
};
export type PricingTableFeatures = PricingTableFeature[];

type PricingFeaturesTableEntityProps = {
	tariffsRes: Awaited<ReturnType<typeof ProductsApi.getProductList>>;
	tariffsResFeatures: Awaited<ReturnType<typeof ProductsApi.getPricingTableDataGrouped>>;
	selectedPriceId?: string;
	trialSelectedPlan?: string;
};

type PricingFeaturesTableEntityPropsV2 = {
	tariffsRes: Awaited<ReturnType<typeof ProductsApi.getNewProductList>>;
	tariffsResFeatures: Awaited<ReturnType<typeof ProductsApi.getPricingTableDataGrouped>>;
	selectedPriceId?: string;
	trialSelectedPlan?: string;
};
export const PricingFeaturesTableEntityV2: Component<PricingFeaturesTableEntityProps> = (props) => {
	const { getPremiumPopUpStore } = useCustomContext(PremiumPopUpContext);

	return (
		<table class="h-1 w-[calc(100%+24px)] table-fixed">
			<thead class="sticky">
				<tr>
					<th />
					<Index
						each={
							props.tariffsRes.productList[getPremiumPopUpStore().activeInterval]
								.products
						}
					>
						{(tariff) => (
							<th
								id={"selected"}
								class={twJoin(
									"border-border-tertiary border-r bg-white px-10 pt-3 pb-1.5 last:border-none",
									tariff().nameId === props.selectedPriceId && ""
								)}
							>
								<div class="flex h-full flex-col items-center justify-center gap-2">
									<h1 class="font-semibold text-lg text-text-primary">
										{tariff().name}
									</h1>
									<TableCheckout
										{...tariff()}
										selectedPriceId={props.selectedPriceId}
									/>
								</div>
							</th>
						)}
					</Index>
				</tr>
			</thead>
			<tbody class="">
				<Index each={props.tariffsResFeatures || []}>
					{(group) => (
						<>
							<tr>
								<td class="-tracking-[0.12px] px-1 pt-9 text-left align-left font-semibold text-2xl text-text-primary">
									{group().groupName}
								</td>
								<For each={new Array(4).fill(0)}>
									{() => (
										<td
											class={
												"border-border-tertiary border-r border-solid px-1 py-4 text-center align-middle font-medium text-sm text-text-primary leading-4 last:border-none"
											}
										/>
									)}
								</For>
							</tr>
							<Index each={group().groupItems}>
								{(rows) => (
									<tr class={"h-14 hover:bg-background-gray-light"}>
										<td class="px-1 py-4 text-left font-medium text-sm text-text-primary leading-4">
											<div class="flex gap-2">
												{rows().name}
												<Tooltip.Root closeDelay={0} openDelay={0}>
													<Tooltip.Trigger
														class="text-text-tertiary"
														asChild={(props) => (
															<InfoIcon {...props()} />
														)}
													/>
													<Portal>
														<Tooltip.Positioner>
															<Tooltip.Content class="z-[200] max-w-56 rounded-md bg-[#2D2D2E] p-2 px-3 py-2 font-medium text-sm text-white leading-5">
																{rows().description ||
																	`IP Adapter XL copies the style and colors of an
														image, helping generate visually consistent pictures`}
																<TipFrameIcon class="-translate-x-1/2 absolute bottom-0 left-1/2 translate-y-full" />
															</Tooltip.Content>
														</Tooltip.Positioner>
													</Portal>
												</Tooltip.Root>
											</div>
										</td>
										<Index each={rows().tariffs}>
											{(rowCell) => (
												<td
													class={twJoin(
														"border-border-tertiary border-r border-solid px-1 py-4 text-center align-middle font-medium text-sm text-text-primary leading-4 last:border-none"
													)}
												>
													<Switch>
														<Match
															when={rowCell().dataType === "string"}
														>
															{rowCell().value}
														</Match>
														<Match
															when={
																rowCell().dataType === "bool" &&
																!rowCell().value
															}
														>
															<div />
														</Match>
														<Match
															when={
																rowCell().dataType === "bool" &&
																rowCell().value
															}
														>
															<EnabledIcon class="inline-block" />
														</Match>
													</Switch>
												</td>
											)}
										</Index>
									</tr>
								)}
							</Index>
						</>
					)}
				</Index>
			</tbody>
		</table>
	);
};
export const PricingFeaturesTableEntityV3: Component<PricingFeaturesTableEntityProps> = (props) => {
	const store = useCustomContext(PricingPageMethods.PricingPageContext);

	return (
		<table class="h-1 w-full table-fixed">
			<thead class="sticky top-0">
				<tr>
					<th />
					<Index
						each={props.tariffsRes.productList[store.getSubscriptionLength()].products}
					>
						{(tariff) => (
							<th
								id={"selected"}
								class={twJoin(
									"border-border-secondary border-r bg-white px-8 pt-3 pb-1.5 last:border-none",
									tariff().nameId === props.selectedPriceId && ""
								)}
							>
								<div class="flex h-full flex-col items-center justify-center gap-3">
									<h1 class="typography-text-17-semibold text-text-primary">
										{tariff().name}
									</h1>
									<TableCheckout
										{...tariff()}
										selectedPriceId={props.selectedPriceId}
									/>
								</div>
							</th>
						)}
					</Index>
				</tr>
			</thead>
			<tbody class="">
				<Index each={props.tariffsResFeatures || []}>
					{(group) => (
						<>
							<tr>
								<td class="header-24-semibold px-1 pt-9 pb-2 text-left align-left text-text-primary">
									{group().groupName}
								</td>
								<For each={new Array(4).fill(0)}>
									{() => (
										<td
											class={
												"border-border-secondary border-r border-solid px-1 py-4 text-center align-middle font-medium text-sm text-text-primary leading-4 last:border-none"
											}
										/>
									)}
								</For>
							</tr>
							<Index each={group().groupItems}>
								{(rows) => (
									<tr class={"h-14 hover:bg-background-gray-light"}>
										<td class="typography-text-14-medium px-1 py-4 text-left text-text-primary">
											<div class="flex gap-2">
												{rows().name}
												<Tooltip.Root closeDelay={0} openDelay={0}>
													<Tooltip.Trigger
														class="text-text-tertiary"
														asChild={(props) => (
															<InfoIcon {...props()} />
														)}
													/>
													<Portal>
														<Tooltip.Positioner>
															<Tooltip.Content class="z-[200] max-w-56 rounded-md bg-[#2D2D2E] p-2 px-3 py-2 font-medium text-sm text-white leading-5">
																{rows().description ||
																	`IP Adapter XL copies the style and colors of an
														image, helping generate visually consistent pictures`}
																<TipFrameIcon class="-translate-x-1/2 absolute bottom-0 left-1/2 translate-y-full" />
															</Tooltip.Content>
														</Tooltip.Positioner>
													</Portal>
												</Tooltip.Root>
											</div>
										</td>
										<Index each={rows().tariffs}>
											{(rowCell) => (
												<td
													class={twJoin(
														"typography-text-14-medium border-border-secondary border-r border-solid px-1 py-4 text-center align-middle text-text-primary last:border-none"
													)}
												>
													<Switch>
														<Match
															when={rowCell().dataType === "string"}
														>
															{rowCell().value}
														</Match>
														<Match
															when={
																rowCell().dataType === "bool" &&
																!rowCell().value
															}
														>
															<div />
														</Match>
														<Match
															when={
																rowCell().dataType === "bool" &&
																rowCell().value
															}
														>
															<CheckedIcon class="inline-block size-4" />
														</Match>
													</Switch>
												</td>
											)}
										</Index>
									</tr>
								)}
							</Index>
						</>
					)}
				</Index>
			</tbody>
		</table>
	);
};
type TableCheckoutProps = {
	selectedPriceId?: string;
	clickHandler?: () => Promise<void>;
} & IProduct;
const TableCheckout: Component<TableCheckoutProps> = (props) => {
	const mode =
		tariffLokUp[getUserStore()?.group || UserSubscriptionPlanEnum.Legacy_Freemium]?.[
			props.nameId
		];
	return (
		<Show when={mode === TariffBtnModeEnum.Current} fallback={<div class="h-10" />}>
			<CheckoutButtonFeatureV2
				{...props}
				btnMode={mode}
				btnProps={{
					variant:
						props.nameId === props.selectedPriceId
							? "actionviolent"
							: "TertiaryOverlay",
					class: "w-max"
				}}
				isSelected={false}
				isTrial={false}
				getBtnText={(isCurrentPlan, btnMode) => {
					if (isCurrentPlan) {
						return "Current plan";
					}
					if (btnMode === TariffBtnModeEnum.Upgrade) {
						return "Upgrade";
					}
					return "Downgrade";
				}}
			/>
		</Show>
	);
};

// New version for refactored pricing structure
export const PricingFeaturesTableEntityV4: Component<PricingFeaturesTableEntityPropsV2> = (
	props
) => {
	const store = useCustomContext(PricingPageContext);
	return (
		<table class="h-1 w-[calc(100%+24px)] table-fixed">
			<thead class="sticky top-0">
				<tr>
					<th />
					<Index each={props.tariffsRes.plans}>
						{(plan) => (
							<th
								id={"selected"}
								class={twJoin(
									"border-border-tertiary border-r bg-white px-10 pt-3 pb-1.5 last:border-none",
									plan().name === props.selectedPriceId && ""
								)}
							>
								<div class="flex h-full flex-col items-center justify-center gap-2">
									<h1 class="font-semibold text-lg text-text-primary">
										{plan().title}
									</h1>
									<TableCheckoutV3
										plan={plan()}
										activeInterval={store.getSubscriptionLength()}
										selectedPriceId={props.selectedPriceId}
									/>
								</div>
							</th>
						)}
					</Index>
				</tr>
			</thead>
			<tbody class="">
				<Index each={props.tariffsResFeatures || []}>
					{(group) => (
						<>
							<tr>
								<td class="-tracking-[0.12px] px-1 pt-9 text-left align-left font-semibold text-2xl text-text-primary">
									{group().groupName}
								</td>
								<For each={new Array(props.tariffsRes.plans.length).fill(0)}>
									{() => (
										<td
											class={
												"border-border-tertiary border-r border-solid px-1 py-4 text-center align-middle font-medium text-sm text-text-primary leading-4 last:border-none"
											}
										/>
									)}
								</For>
							</tr>
							<Index each={group().groupItems}>
								{(rows) => (
									<tr class={"h-14 hover:bg-background-gray-light"}>
										<td class="px-1 py-4 text-left font-medium text-sm text-text-primary leading-4">
											<div class="flex gap-2">
												{rows().name}
												<StyledTooltip.TooltipClosedComponent
													content={
														<div class="text-white">
															{rows().description}
														</div>
													}
													showArrow
												>
													{(p) => (
														<InfoIcon
															{...(p as any)}
															class="text-text-tertiary"
														/>
													)}
												</StyledTooltip.TooltipClosedComponent>
											</div>
										</td>
										<Index each={rows().tariffs}>
											{(rowCell) => (
												<td
													class={twJoin(
														"border-border-tertiary border-r border-solid px-1 py-4 text-center align-middle font-medium text-sm text-text-primary leading-4 last:border-none"
													)}
												>
													<Switch>
														<Match
															when={rowCell().dataType === "string"}
														>
															{rowCell().value}
														</Match>
														<Match
															when={
																rowCell().dataType === "bool" &&
																!rowCell().value
															}
														>
															<div />
														</Match>
														<Match
															when={
																rowCell().dataType === "bool" &&
																rowCell().value
															}
														>
															<EnabledIcon class="inline-block" />
														</Match>
													</Switch>
												</td>
											)}
										</Index>
									</tr>
								)}
							</Index>
						</>
					)}
				</Index>
			</tbody>
		</table>
	);
};

type TableCheckoutV2Props = {
	plan: Awaited<ReturnType<typeof ProductsApi.getNewProductList>>["plans"][0];
	activeInterval: string;
	selectedPriceId?: string;
	clickHandler?: () => Promise<void>;
};

const TableCheckoutV3: Component<TableCheckoutV2Props> = (props) => {
	const mode =
		tariffLokUp[getUserStore()?.group || UserSubscriptionPlanEnum.Legacy_Freemium]?.[
			props.plan.name as UserSubscriptionPlanEnum
		];

	const _activeCost = getActiveCostByInterval(props.plan.costs, props.activeInterval);

	return (
		<CheckoutButtonFeatureV2
			// Map new structure to old props for compatibility
			name={props.plan.title}
			nameId={props.plan.name as UserSubscriptionPlanEnum}
			isSelected={false}
			isTrial={false}
			btnMode={mode}
			btnProps={{
				variant:
					props.plan.name === props.selectedPriceId ? "actionviolent" : "TertiaryOverlay",
				class: "w-max"
			}}
			getBtnText={(isCurrentPlan, btnMode) => {
				if (isCurrentPlan) {
					return "Current plan";
				}
				if (btnMode === TariffBtnModeEnum.Upgrade) {
					return "Upgrade";
				}
				return "Downgrade";
			}}
		/>
	);
};

// Mobile version for V4 using divs and flexbox
export const MobilePricingFeaturesTableEntityV4: Component<PricingFeaturesTableEntityPropsV2> = (
	props
) => {
	return (
		<div class="flex w-full flex-col gap-3 bg-white">
			{/* Header with tariff names */}
			<div class="typography-text-17-semibold sticky top-0 z-10 flex w-full items-center justify-between bg-white p-3 text-text-primary">
				<Index each={props.tariffsRes.plans}>
					{(plan) => (
						<div class="min-w-12 text-center text-text-primary">{plan().title}</div>
					)}
				</Index>
			</div>

			{/* Groups and features */}
			<div class="flex flex-col gap-3">
				<Index each={props.tariffsResFeatures || []}>
					{(group) => (
						<div class="flex flex-col">
							<div class="flex w-full items-center justify-center bg-white py-4 pt-6 pb-1 font-semibold text-3xl text-text-primary leading-8">
								{group().groupName}
							</div>

							{/* Group items */}
							<div class="flex flex-col gap-3">
								<Index each={group().groupItems}>
									{(rows) => (
										<div class="flex flex-col">
											{/* Feature name */}
											<div class="flex w-full items-center justify-center py-4">
												<span class="font-medium text-text-primary text-xl leading-8">
													{rows().name}
												</span>
											</div>

											{/* Feature values for each tariff */}
											<div class="flex items-center justify-between gap-3">
												<Index each={rows().tariffs}>
													{(rowCell) => (
														<div
															class={twJoin(
																"flex min-w-12 flex-1 items-center justify-center px-3 py-2",
																rowCell().dataType === "string" &&
																	"border-border-secondary border-r"
															)}
														>
															<Switch>
																<Match
																	when={
																		rowCell().dataType ===
																		"string"
																	}
																>
																	<span class="text-center font-medium text-lg text-text-primary md:font-normal md:text-sm">
																		{rowCell().value}
																	</span>
																</Match>
																<Match
																	when={
																		rowCell().dataType ===
																			"bool" &&
																		!rowCell().value
																	}
																>
																	<div class="size-4" />
																</Match>
																<Match
																	when={
																		rowCell().dataType ===
																			"bool" &&
																		rowCell().value
																	}
																>
																	<svg
																		width="33"
																		height="32"
																		viewBox="0 0 33 32"
																		fill="none"
																		xmlns="http://www.w3.org/2000/svg"
																	>
																		<path
																			d="M27.1615 8L12.4948 22.6667L5.82812 16"
																			stroke="#2D2D2D"
																			stroke-width="4"
																			stroke-linecap="round"
																			stroke-linejoin="round"
																		/>
																	</svg>
																</Match>
															</Switch>
														</div>
													)}
												</Index>
											</div>
										</div>
									)}
								</Index>
							</div>
						</div>
					)}
				</Index>
			</div>
		</div>
	);
};
